import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../../partials/Header';
import Footer from '../../partials/Footer';
import PageIllustration from '../../partials/PageIllustration';
import { IPFS_GATEWAYS, parseIpfsInput, buildIpfsUrls } from '../../utils/ipfs';

const EXAMPLES = [
  { label: 'ipfs:// URI', value: 'ipfs://bafybeigdyrzt5sfp7udm7hu76uh7y26nf3efuylqabf3oclgtqy55fbzdi' },
  { label: 'Path gateway URL', value: 'https://ipfs.io/ipfs/QmbWqxBEKC3P8tqsKc98xmWNzrzDtRLMiMPL8wBuTGsMnR' },
  { label: 'Subdomain URL', value: 'https://bafybeigdyrzt5sfp7udm7hu76uh7y26nf3efuylqabf3oclgtqy55fbzdi.ipfs.dweb.link/' },
  { label: 'IPNS name', value: 'ipns://docs.ipfs.tech' },
];

function OutputRow({ label, value, canOpen = false }) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-gray-300 text-sm font-medium">{label}</span>
        <span className="flex gap-2">
          {canOpen && (
            <a href={value} target="_blank" rel="noopener noreferrer" className="text-xs px-2 py-1 rounded bg-gray-600 text-gray-300 hover:bg-gray-500 transition-colors">
              Open
            </a>
          )}
          <button
            type="button"
            onClick={() => {
              navigator.clipboard.writeText(value);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }}
            className="text-xs px-2 py-1 rounded bg-gray-600 text-gray-300 hover:bg-gray-500 transition-colors"
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </span>
      </div>
      <div className="bg-gray-700 rounded-md px-4 py-3">
        <code className="text-purple-300 font-mono text-sm break-all">{value}</code>
      </div>
    </div>
  );
}

function IpfsUrlConverter() {
  const [input, setInput] = useState('');
  const [gateway, setGateway] = useState('ipfs.io');
  const [parsed, setParsed] = useState(null);
  const [urls, setUrls] = useState(null);
  const [error, setError] = useState('');

  const convert = (value, gw) => {
    try {
      const p = parseIpfsInput(value ?? input);
      const u = buildIpfsUrls({ kind: p.kind, ref: p.ref, path: p.path, gateway: gw ?? gateway });
      setParsed(p);
      setUrls(u);
      setError('');
    } catch (e) {
      setParsed(null);
      setUrls(null);
      setError(e.message);
    }
  };

  const changeGateway = (gw) => {
    setGateway(gw);
    if (parsed) convert(input, gw);
  };

  return (
    <div className="flex flex-col min-h-screen overflow-hidden">
      <Helmet>
        <title>IPFS URL Converter — ipfs:// ↔ Gateway Links</title>
        <meta name="description" content="Convert ipfs:// and ipns:// URIs to HTTP gateway URLs — path and subdomain forms for ipfs.io, dweb.link and more. Free, no signup, in-browser." />
        <meta name="keywords" content="ipfs url converter, ipfs to https, ipfs gateway url, ipfs link converter, ipns to url, ipfs uri to link" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.ogtechnologies.co/tools/ipfs-url-converter/" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.ogtechnologies.co/tools/ipfs-url-converter/" />
        <meta property="og:title" content="IPFS URL Converter — ipfs:// ↔ Gateway Links" />
        <meta property="og:description" content="Convert ipfs:// and ipns:// URIs to HTTP gateway URLs — path and subdomain forms. Free, in-browser." />
        <meta property="og:image" content="https://www.ogtechnologies.co/og-og-image.png" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.ogtechnologies.co/tools/ipfs-url-converter/" />
        <meta name="twitter:title" content="IPFS URL Converter — ipfs:// ↔ Gateway Links" />
        <meta name="twitter:description" content="Convert ipfs:// and ipns:// URIs to HTTP gateway URLs — path and subdomain forms. Free, in-browser." />
        <meta name="twitter:image" content="https://www.ogtechnologies.co/og-og-image.png" />
      </Helmet>

      <Header />

      <main className="grow">
        <div className="relative max-w-6xl mx-auto h-0 pointer-events-none" aria-hidden="true">
          <PageIllustration />
        </div>

        <section className="relative">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="pt-32 pb-12 md:pt-40 md:pb-20">
              <div className="max-w-3xl mx-auto text-center pb-12 md:pb-16">
                <h1 className="h1">IPFS URL Converter</h1>
                <p className="text-xl text-gray-400 mt-4">
                  Convert between <code className="text-purple-300">ipfs://</code> / <code className="text-purple-300">ipns://</code> URIs
                  and HTTP gateway URLs — both path-style and origin-isolated subdomain forms.
                </p>
              </div>

              <div className="max-w-4xl mx-auto">
                <div className="bg-gray-800 rounded-lg p-8 border border-gray-700">
                  <div className="mb-4">
                    <label className="block text-gray-300 text-sm font-medium mb-1">CID, IPNS name, ipfs:// URI, or gateway URL</label>
                    <input
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') convert(); }}
                      className="w-full bg-gray-700 text-white px-4 py-2 rounded-md border border-gray-600 focus:border-purple-500 focus:outline-none font-mono text-sm"
                      placeholder="ipfs://bafybei…, bafybei…/path/file.png, or a gateway URL"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Try:{' '}
                      {EXAMPLES.map((ex, i) => (
                        <span key={ex.label}>
                          {i > 0 && ' · '}
                          <button
                            type="button"
                            className="text-purple-400 hover:text-purple-300"
                            onClick={() => { setInput(ex.value); convert(ex.value); }}
                          >
                            {ex.label}
                          </button>
                        </span>
                      ))}
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 mb-4">
                    <button
                      type="button"
                      onClick={() => convert()}
                      disabled={!input.trim()}
                      className="flex-1 px-4 py-3 rounded-md bg-purple-600 text-white font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Convert
                    </button>
                    <div className="sm:w-64">
                      <label className="sr-only">Gateway</label>
                      <select
                        value={gateway}
                        onChange={(e) => changeGateway(e.target.value)}
                        className="w-full bg-gray-700 text-white px-3 py-3 rounded-md border border-gray-600 focus:border-purple-500 focus:outline-none text-sm"
                      >
                        {IPFS_GATEWAYS.map((g) => (
                          <option key={g.host} value={g.host}>{g.host}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {error && <p className="text-sm text-red-400 mb-4">{error}</p>}

                  {parsed && urls && (
                    <div className="space-y-4">
                      <div className="grid sm:grid-cols-2 gap-3 text-sm">
                        <div className="bg-gray-900/50 rounded-md p-3 border border-gray-700">
                          <p className="text-gray-500 text-xs mb-1">Detected</p>
                          <p className="text-gray-200">{parsed.kind === 'ipfs' ? 'IPFS content (CID)' : 'IPNS name'} — {parsed.style.replace('-', ' ')}</p>
                        </div>
                        <div className="bg-gray-900/50 rounded-md p-3 border border-gray-700">
                          <p className="text-gray-500 text-xs mb-1">Path</p>
                          <p className="text-gray-200 font-mono break-all">{parsed.path || '(root)'}</p>
                        </div>
                      </div>

                      <OutputRow label={`Canonical ${parsed.kind}:// URI`} value={urls.canonical} />
                      <OutputRow label="Path gateway URL" value={urls.pathGateway} canOpen />
                      {urls.subdomain && (
                        <OutputRow label="Subdomain gateway URL (origin-isolated)" value={urls.subdomain} canOpen />
                      )}
                      {!urls.subdomain && (
                        <p className="text-xs text-gray-500">
                          No subdomain form — {parsed.kind === 'ipfs' ? 'this CID is too long for a DNS label' : 'this IPNS name is not a valid DNS label'}; use the path-style URL.
                        </p>
                      )}

                      {urls.cidInfo && (
                        <p className="text-xs text-gray-500">
                          CIDv{urls.cidInfo.version} · {urls.cidInfo.codec} · {urls.cidInfo.hashFunction} —{' '}
                          <Link to="/tools/ipfs-cid-inspector/" className="text-purple-400 hover:text-purple-300">inspect it</Link>
                        </p>
                      )}
                      {parsed.kind === 'ipns' && (
                        <p className="text-xs text-gray-500">
                          IPNS names are mutable pointers (peer IDs, public keys, or DNSLink domains) — the URL resolves to whatever content the name currently points to.
                        </p>
                      )}
                    </div>
                  )}

                  <div className="mt-6 text-xs text-gray-500 space-y-1">
                    <p><strong>Path gateway</strong> (<code>host/ipfs/cid</code>) works everywhere but shares one origin across all content.</p>
                    <p><strong>Subdomain gateway</strong> (<code>cid.ipfs.host</code>) gives each CID its own origin — required for websites with relative links, cookies, or localStorage.</p>
                  </div>
                </div>

                <div className="mt-6 text-center text-sm text-gray-500">
                  More IPFS tools:{' '}
                  <Link to="/tools/ipfs-cid-inspector/" className="text-purple-400 hover:text-purple-300">CID Inspector</Link>
                  {' · '}
                  <Link to="/tools/ipfs-gateway-checker/" className="text-purple-400 hover:text-purple-300">Gateway Checker</Link>
                  {' · '}
                  <Link to="/tools/ipfs-cid-generator/" className="text-purple-400 hover:text-purple-300">File → CID</Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default IpfsUrlConverter;
